import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { useAuth, hasPagePermission } from '../context/AuthContext';
import api from '../services/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { 
  Calendar, 
  Plus, 
  CheckCircle2, 
  Circle, 
  ChevronRight,
  ClipboardList,
  Search,
  Filter,
  Clock,
  AlertCircle
} from 'lucide-react';

export const TasksPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewEventDialog, setShowNewEventDialog] = useState(false);
  const [newEvent, setNewEvent] = useState({ name: '', description: '', date: '' });
  const [creating, setCreating] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all'); // all, active, completed
  
  const canCreateTasks = true; // Všichni uživatelé mají přístup

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    try {
      setLoading(true);
      const response = await api.get('/task-events');
      setEvents(response.data);
    } catch (error) {
      console.error('Error fetching events:', error);
      toast.error('Chyba při načítání událostí');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateEvent = async () => {
    if (!newEvent.name.trim()) {
      toast.error('Zadejte název události');
      return;
    }
    if (!newEvent.date) {
      toast.error('Zadejte datum události');
      return;
    }

    try {
      setCreating(true);
      const response = await api.post('/task-events', newEvent);
      setEvents([response.data, ...events]);
      setShowNewEventDialog(false);
      setNewEvent({ name: '', description: '', date: '' });
      toast.success('Událost vytvořena');
      navigate(`/ukoly/${response.data.id}`);
    } catch (error) {
      console.error('Error creating event:', error);
      toast.error('Chyba při vytváření události');
    } finally {
      setCreating(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const parts = dateStr.split('-');
    if (parts.length === 3) {
      return `${parts[2]}.${parts[1]}.${parts[0]}`;
    }
    return dateStr;
  };

  const isOverdue = (event) => {
    const today = new Date().toISOString().split('T')[0];
    return event.date < today && event.task_count > event.completed_count;
  };

  const isPending = (event) => {
    return event.task_count > event.completed_count;
  };

  const filteredEvents = events.filter(event => {
    // Search filter
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      if (!event.name.toLowerCase().includes(search) && 
          !(event.description || '').toLowerCase().includes(search)) {
        return false;
      }
    }
    
    // Status filter
    if (filterStatus === 'active' && event.task_count === event.completed_count && event.task_count > 0) {
      return false;
    }
    if (filterStatus === 'completed' && (event.task_count === 0 || event.task_count !== event.completed_count)) {
      return false;
    }
    
    return true;
  });

  return (
    <Layout>
      <div className="p-6 max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
              <ClipboardList className="w-7 h-7 text-blue-600" />
              Úkoly
            </h1>
            <p className="text-slate-500 mt-1">Správa událostí a úkolů</p>
          </div>
          
          {canCreateTasks && (
            <Button 
              onClick={() => setShowNewEventDialog(true)}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="new-event-button"
            >
              <Plus className="w-4 h-4 mr-2" />
              Nová událost
            </Button>
          )}
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl shadow-sm border p-4 mb-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Hledat události..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="search-events-input"
              />
            </div>
            <div className="flex gap-2">
              <Button
                variant={filterStatus === 'all' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setFilterStatus('all')}
              >
                Vše
              </Button>
              <Button
                variant={filterStatus === 'active' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setFilterStatus('active')}
              >
                <Clock className="w-4 h-4 mr-1" />
                Aktivní
              </Button>
              <Button
                variant={filterStatus === 'completed' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setFilterStatus('completed')}
              >
                <CheckCircle2 className="w-4 h-4 mr-1" />
                Dokončené
              </Button>
            </div>
          </div>
        </div>

        {/* Events List */}
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : filteredEvents.length === 0 ? (
          <div className="bg-white rounded-xl shadow-sm border p-12 text-center">
            <ClipboardList className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-slate-600 mb-2">
              {searchTerm || filterStatus !== 'all' ? 'Žádné události nenalezeny' : 'Zatím žádné události'}
            </h3>
            <p className="text-slate-400 mb-4">
              {canCreateTasks ? 'Vytvořte první událost pro zadávání úkolů' : 'Čekejte na přiřazení úkolů'}
            </p>
            {canCreateTasks && !searchTerm && filterStatus === 'all' && (
              <Button onClick={() => setShowNewEventDialog(true)} className="bg-blue-600 hover:bg-blue-700">
                <Plus className="w-4 h-4 mr-2" />
                Vytvořit událost
              </Button>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            {filteredEvents.map((event) => (
              <div
                key={event.id}
                onClick={() => navigate(`/ukoly/${event.id}`)}
                className={`bg-white rounded-xl shadow-sm border p-5 cursor-pointer hover:shadow-md transition-all ${
                  isOverdue(event) ? 'border-l-4 border-l-red-500' : ''
                }`}
                data-testid={`event-card-${event.id}`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-lg font-semibold text-slate-800">{event.name}</h3>
                      {isOverdue(event) && (
                        <span className="px-2 py-0.5 bg-red-100 text-red-600 text-xs font-medium rounded-full flex items-center gap-1">
                          <AlertCircle className="w-3 h-3" />
                          Po termínu
                        </span>
                      )}
                    </div>
                    
                    <div className="flex items-center gap-4 text-sm text-slate-500">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        {formatDate(event.date)}
                      </span>
                      
                      {event.task_count > 0 ? (
                        <span className="flex items-center gap-1">
                          {event.completed_count === event.task_count ? (
                            <CheckCircle2 className="w-4 h-4 text-green-500" />
                          ) : (
                            <Circle className="w-4 h-4 text-slate-400" />
                          )}
                          {event.completed_count}/{event.task_count} úkolů
                        </span>
                      ) : (
                        <span className="text-slate-400">Žádné úkoly</span>
                      )}
                    </div>
                    
                    {event.description && (
                      <p className="text-sm text-slate-500 mt-2 line-clamp-1">{event.description}</p>
                    )}
                  </div>
                  
                  <ChevronRight className="w-5 h-5 text-slate-400" />
                </div>
                
                {/* Progress bar */}
                {event.task_count > 0 && (
                  <div className="mt-4">
                    <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                      <div 
                        className={`h-full transition-all ${
                          event.completed_count === event.task_count ? 'bg-green-500' : 'bg-blue-500'
                        }`}
                        style={{ width: `${(event.completed_count / event.task_count) * 100}%` }}
                      />
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* New Event Dialog */}
        <Dialog open={showNewEventDialog} onOpenChange={setShowNewEventDialog}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Nová událost</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label htmlFor="event-name">Název události *</Label>
                <Input
                  id="event-name"
                  value={newEvent.name}
                  onChange={(e) => setNewEvent({ ...newEvent, name: e.target.value })}
                  placeholder="např. Kontrola hygieny"
                  data-testid="event-name-input"
                />
              </div>
              <div>
                <Label htmlFor="event-date">Datum události *</Label>
                <Input
                  id="event-date"
                  type="date"
                  value={newEvent.date}
                  onChange={(e) => setNewEvent({ ...newEvent, date: e.target.value })}
                  data-testid="event-date-input"
                />
              </div>
              <div>
                <Label htmlFor="event-description">Popis</Label>
                <Textarea
                  id="event-description"
                  value={newEvent.description}
                  onChange={(e) => setNewEvent({ ...newEvent, description: e.target.value })}
                  placeholder="Volitelný popis události..."
                  rows={3}
                  data-testid="event-description-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowNewEventDialog(false)}>
                Zrušit
              </Button>
              <Button 
                onClick={handleCreateEvent} 
                disabled={creating}
                className="bg-blue-600 hover:bg-blue-700"
                data-testid="create-event-submit"
              >
                {creating ? 'Vytváření...' : 'Vytvořit'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default TasksPage;
